import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import {
  createOrder,
  fetchMenuCategories,
  fetchMenuItems,
  type MenuItem,
  type OrderItem,
} from '../../lib/api';

export default function OrderPage() {
  const navigate = useNavigate();
  const [cart, setCart] = useState<OrderItem[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [categories, setCategories] = useState<string[]>(['All']);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [isSubmittingOtherPayment, setIsSubmittingOtherPayment] = useState(false);
  const [showOtherPaymentModal, setShowOtherPaymentModal] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    async function loadMenu() {
      try {
        setIsLoading(true);
        const [categoriesResponse, itemsResponse] = await Promise.all([
          fetchMenuCategories(),
          fetchMenuItems(false),
        ]);

        if (!isMounted) {
          return;
        }

        setCategories(['All', ...categoriesResponse.categories.map((category) => category.name)]);
        setMenuItems(itemsResponse.items);
        setError(null);
      } catch (loadError) {
        if (!isMounted) {
          return;
        }

        setError(loadError instanceof Error ? loadError.message : 'Unable to load menu.');
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadMenu();

    return () => {
      isMounted = false;
    };
  }, []);

  const filteredItems = selectedCategory === 'All'
    ? menuItems
    : menuItems.filter(item => item.category === selectedCategory);

  const addToCart = (item: MenuItem) => {
    const existingItem = cart.find(cartItem => cartItem.id === item.id);

    if (existingItem) {
      setCart(cart.map(cartItem =>
        cartItem.id === item.id
          ? { ...cartItem, quantity: cartItem.quantity + 1 }
          : cartItem
      ));
      return;
    }

    setCart([...cart, { id: item.id, name: item.name, price: item.price, quantity: 1 }]);
  };

  const updateQuantity = (id: number, change: number) => {
    setCart(cart.map(item => {
      if (item.id === id) {
        const newQuantity = item.quantity + change;
        return newQuantity > 0 ? { ...item, quantity: newQuantity } : item;
      }

      return item;
    }).filter(item => item.quantity > 0));
  };

  const removeFromCart = (id: number) => {
    setCart(cart.filter(item => item.id !== id));
  };

  const total = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  const handleCheckout = () => {
    if (cart.length > 0) {
      navigate('/checkout', {
        state: {
          cart,
          total,
          customerName,
          customerPhone,
        },
      });
    }
  };

  const handleOtherPaymentClick = () => {
    if (cart.length === 0) {
      setError('Add items to the cart first.');
      return;
    }

    if (!customerName.trim()) {
      setError('Enter customer name first.');
      return;
    }

    setError(null);
    setShowOtherPaymentModal(true);
  };

  const handleOtherPayment = async (paymentMethod: 'cash' | 'card') => {
    setShowOtherPaymentModal(false);

    try {
      setError(null);
      setIsSubmittingOtherPayment(true);

      await createOrder({
        customerName: customerName.trim(),
        customerPhone: customerPhone.trim(),
        paymentMethod,
        cart,
      });

      setCart([]);
      navigate('/live-orders');
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Unable to create the paid order.');
    } finally {
      setIsSubmittingOtherPayment(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {showOtherPaymentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Confirm Payment Method</h3>
            <p className="text-sm text-gray-600 mb-5">
              Choose how this order was paid. Cash or Credit Card will add it to live orders.
            </p>

            <div className="space-y-3">
              <button
                onClick={() => handleOtherPayment('card')}
                disabled={isSubmittingOtherPayment}
                className="w-full rounded-lg bg-slate-700 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-slate-800"
              >
                Credit Card
              </button>
              <button
                onClick={() => handleOtherPayment('cash')}
                disabled={isSubmittingOtherPayment}
                className="w-full rounded-lg bg-amber-500 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-amber-600"
              >
                Cash
              </button>
              <button
                onClick={() => setShowOtherPaymentModal(false)}
                disabled={isSubmittingOtherPayment}
                className="w-full rounded-lg bg-gray-100 px-4 py-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Menu Items</h2>

              <div className="flex flex-wrap gap-2 mb-6">
                {categories.map(category => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      selectedCategory === category
                        ? 'bg-teal-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>

              {isLoading ? (
                <div className="py-16 text-center text-sm text-gray-500">Loading menu...</div>
              ) : error ? (
                <div className="py-16 text-center">
                  <p className="text-sm text-red-600 mb-4">{error}</p>
                  <button
                    onClick={() => window.location.reload()}
                    className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg text-sm"
                  >
                    Retry
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {filteredItems.map(item => (
                    <div
                      key={item.id}
                      onClick={() => item.available && addToCart(item)}
                      className={`bg-white border border-gray-200 rounded-lg overflow-hidden transition-all hover:shadow-md ${
                        item.available ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'
                      }`}
                    >
                      <div className="w-full h-32 sm:h-40 bg-gray-100">
                        {item.image ? (
                          <img
                            src={item.image}
                            alt={item.name}
                            className="w-full h-full object-contain object-center bg-gray-100"
                          />
                        ) : (
                          <div className="flex h-full items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 px-3 text-center">
                            <span className="text-sm font-semibold text-gray-600">{item.name}</span>
                          </div>
                        )}
                      </div>
                      <div className="p-3">
                        <h3 className="font-semibold text-sm text-gray-900 mb-1">{item.name}</h3>
                        <p className="text-xs text-gray-500 mb-2">{item.category}</p>
                        <p className="text-base font-bold text-teal-600">KSh {item.price.toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-6 sticky top-24">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Current Order</h2>

              {cart.length === 0 ? (
                <div className="text-center py-12">
                  <i className="ri-shopping-cart-line text-5xl text-gray-300 w-16 h-16 flex items-center justify-center mx-auto mb-3"></i>
                  <p className="text-sm text-gray-500">No items in cart</p>
                </div>
              ) : (
                <>
                  <div className="space-y-3 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Customer Name</label>
                      <input
                        type="text"
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                        placeholder="Enter customer name"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                      <input
                        type="tel"
                        value={customerPhone}
                        onChange={(e) => setCustomerPhone(e.target.value)}
                        placeholder="Optional for cash/card orders"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      />
                    </div>
                  </div>

                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 mb-4">
                      {error}
                    </div>
                  )}

                  <div className="space-y-3 mb-6 max-h-96 overflow-y-auto">
                    {cart.map(item => (
                      <div key={item.id} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                        <div className="flex-1">
                          <h4 className="font-medium text-sm text-gray-900">{item.name}</h4>
                          <p className="text-sm text-gray-600">KSh {item.price.toLocaleString()}</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => updateQuantity(item.id, -1)}
                            className="w-7 h-7 flex items-center justify-center bg-white border border-gray-300 rounded-md hover:bg-gray-100 cursor-pointer"
                          >
                            <i className="ri-subtract-line text-sm"></i>
                          </button>
                          <span className="w-8 text-center font-medium text-sm">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item.id, 1)}
                            className="w-7 h-7 flex items-center justify-center bg-white border border-gray-300 rounded-md hover:bg-gray-100 cursor-pointer"
                          >
                            <i className="ri-add-line text-sm"></i>
                          </button>
                          <button
                            onClick={() => removeFromCart(item.id)}
                            className="w-7 h-7 flex items-center justify-center bg-red-50 text-red-600 rounded-md hover:bg-red-100 cursor-pointer ml-2"
                          >
                            <i className="ri-delete-bin-line text-sm"></i>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-gray-200 pt-4 mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-gray-600">Subtotal</span>
                      <span className="text-sm font-medium">KSh {total.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-base font-semibold text-gray-900">Total</span>
                      <span className="text-xl font-bold text-teal-600">KSh {total.toLocaleString()}</span>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <button
                      onClick={handleCheckout}
                      disabled={isSubmittingOtherPayment}
                      className={`w-full py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                        isSubmittingOtherPayment
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-teal-500 text-white hover:bg-teal-600'
                      }`}
                    >
                      Pay with M-Pesa
                    </button>

                    <button
                      onClick={handleOtherPaymentClick}
                      disabled={isSubmittingOtherPayment}
                      className={`w-full py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                        isSubmittingOtherPayment
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-amber-500 text-white hover:bg-amber-600'
                      }`}
                    >
                      {isSubmittingOtherPayment ? 'Creating Paid Order...' : 'Paid with Other Method'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
